/**
 * Card — shadcn Card 어댑터. 기존 title/extra/compact/onClick API 유지.
 *
 * title 또는 extra가 있을 때만 헤더 영역을 렌더링한다.
 * onClick이 있으면 role="button" + 키보드(Enter/Space) 지원.
 * compact면 CardContent 패딩을 줄여 조밀하게 표시한다.
 */

import { ReactNode, KeyboardEvent } from 'react'
import {
  Card as ShadCard,
  CardHeader,
  CardTitle,
  CardAction,
  CardContent,
} from '@/components/ui/card'
import { cn } from './cn'

interface CardProps {
  /** 카드 헤더 제목 텍스트 */
  title?: string
  /** 헤더 우측에 배치할 노드 (버튼, 뱃지 등) */
  extra?: ReactNode
  className?: string
  /** 클릭 시 호출 — 지정 시 카드가 role="button" 으로 동작 (Enter/Space 키 지원) */
  onClick?: () => void
  /** body 패딩을 줄여 콘텐츠 영역을 넓게 사용 (대시보드 타일 등 조밀한 카드용) */
  compact?: boolean
  children: ReactNode
}

export function Card({ title, extra, className, onClick, compact, children }: CardProps) {
  const hasHeader = Boolean(title ?? extra)

  const interactiveProps = onClick
    ? {
        role: 'button' as const,
        tabIndex: 0,
        onClick,
        onKeyDown: (e: KeyboardEvent) => {
          if (e.key === 'Enter' || e.key === ' ') {
            e.preventDefault()
            onClick()
          }
        },
      }
    : {}

  return (
    <ShadCard className={cn('gap-0', className)} {...interactiveProps}>
      {hasHeader && (
        <CardHeader className="px-6 py-4">
          {title && <CardTitle>{title}</CardTitle>}
          {extra && <CardAction>{extra}</CardAction>}
        </CardHeader>
      )}
      <CardContent className={cn(compact ? 'px-4 py-3' : 'px-6 py-4')}>
        {children}
      </CardContent>
    </ShadCard>
  )
}
